import React, { useState, useEffect } from "react";
import {
  Container,
  Alert,
  Card,
  Spinner,
  Row,
  Col,
  Badge,
  Button,
  ProgressBar,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Calendar,
  Clock,
  BarChart2,
  ThumbsUp,
  MessageSquare,
  Image,
  Video,
} from "lucide-react";
import api from "../api/api";
import { getDisciplineIcon } from "../constants";

const ExerciseFeedbackList = () => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { exerciseId } = useParams();
  const navigate = useNavigate();

  const pinkColor = "#FF69B4";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [exerciseResponse, feedbackResponse] = await Promise.all([
          api.get(`/exercises/${exerciseId}/`),
          api.get(`/feedback/?exercise=${exerciseId}`),
        ]);

        setExercise(exerciseResponse.data);
        setFeedbackList(feedbackResponse.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching feedback:", err);
        setError("Failed to load activity feedback");
        setLoading(false);
      }
    };

    fetchData();
  }, [exerciseId]);

  const getEffortColor = (effort) => {
    if (effort <= 3) return "success";
    if (effort <= 7) return "warning";
    return "danger";
  };

  const getEffortEmoji = (effort) => {
    if (effort <= 3) return "😴";
    if (effort <= 7) return "😅";
    return "😤";
  };

  const formatDateAU = (date) => {
    return new Date(date).toLocaleDateString("en-AU", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatShortDateAU = (date) => {
    return new Date(date).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <Spinner animation="border" className="text-custom-pink mb-3" />
          <h5 className="text-muted">Loading feedback history...</h5>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="pb-5">
      {/* Header Section */}
      <div className="mb-5">
        <Button
          variant="link"
          className="text-decoration-none mb-4 p-0 d-flex align-items-center"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft color={pinkColor} size={24} className="me-1" />
          <span className="text-primary">Back to Program</span>
        </Button>

        <div className="d-flex justify-content-center align-items-center flex-wrap gap-3 mb-4">
          <div className="d-flex align-items-center gap-3">
            {getDisciplineIcon(exercise.discipline, 24)}
            <h2 className="mb-0 display-6 fw-bold">{exercise?.name}</h2>
          </div>
          <Badge bg="custom-pink" className="px-4 py-2 fs-6">
            {feedbackList.length}{" "}
            {feedbackList.length === 1 ? "Entry" : "Entries"}
          </Badge>
        </div>
      </div>

      {/* Stats Summary */}
      {feedbackList.length > 0 && (
        <Card className="mb-5 border-0 shadow-sm">
          <Card.Body className="py-3">
            <Row className="text-center g-4">
              <Col md={4} className="border-end">
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <Calendar color={pinkColor} size={28} />
                  <div>
                    <h6 className="mb-2 fw-bold">First Entry</h6>
                    <p className="mb-0 text-muted">
                      {formatShortDateAU(feedbackList[feedbackList.length-1].date_completed)}
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={4} className="border-end">
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <BarChart2 color={pinkColor} size={28} />
                  <div>
                    <h6 className="mb-2 fw-bold">Average Effort</h6>
                    <p className="mb-0 text-muted">
                      {(
                        feedbackList.reduce(
                          (acc, curr) => acc + curr.effort_rating,
                          0
                        ) / feedbackList.length
                      ).toFixed(1)}
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <Clock color={pinkColor} size={28} />
                  <div>
                    <h6 className="mb-2 fw-bold">Latest Entry</h6>
                    <p className="mb-0 text-muted">
                      {formatShortDateAU(feedbackList[0].date_completed)}
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Feedback List */}
      {feedbackList.length === 0 ? (
        <Alert variant="info" className="d-flex align-items-center p-2">
          <i className="bi bi-info-circle-fill me-3"></i>
          No feedback has been submitted for this activity yet.
        </Alert>
      ) : (
        <div className="feedback-list">
          {feedbackList.map((feedback) => (
            <Card key={feedback.id} className="mb-4 border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom py-4">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <Calendar color={pinkColor} size={24} />
                  <h5 className="mb-0 fw-bold">
                    {formatDateAU(feedback.date_completed)}
                  </h5>
                </div>

                {/* Effort Level */}
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 fw-bold">
                      <ThumbsUp color={pinkColor} size={20} className="me-2" />
                      Effort Level
                    </h6>
                    <span className="fs-5">
                      {getEffortEmoji(feedback.effort_rating)}
                    </span>
                  </div>
                  <ProgressBar
                    now={feedback.effort_rating * 10}
                    variant={getEffortColor(feedback.effort_rating)}
                    className="progress-tall"
                    style={{ height: "1.5rem" }}
                  />
                  <div className="d-flex justify-content-between mt-1">
                    <small className="text-muted">Easy</small>
                    <small className="text-muted fw-bold">
                      {feedback.effort_rating}/10
                    </small>
                    <small className="text-muted">Hard</small>
                  </div>
                </div>
              </Card.Header>

              <Card.Body className="bg-light rounded-bottom p-4">
                {/* Feeling/Notes Section */}
                {feedback.feeling && (
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">
                      <MessageSquare
                        color={pinkColor}
                        size={20}
                        className="me-2"
                      />
                      How they felt
                    </h6>
                    <Card className="border-0">
                      <Card.Body className="py-3">
                        <p className="mb-0 text-muted">{feedback.feeling}</p>
                      </Card.Body>
                    </Card>
                  </div>
                )}

                {/* Media Section */}
                {(feedback.image_url || feedback.video_url) && (
                  <div>
                    <h6 className="fw-bold mb-3">
                      {feedback.image_url ? (
                        <Image color={pinkColor} size={20} className="me-2" />
                      ) : (
                        <Video color={pinkColor} size={20} className="me-2" />
                      )}
                      Feedback Media
                    </h6>
                    <div className="media-container rounded overflow-hidden">
                      {feedback.image_url && (
                        <img
                          src={feedback.image_url}
                          alt="Progress"
                          className="img-fluid rounded"
                        />
                      )}
                      {feedback.video_url && (
                        <video
                          key={feedback.video_url}
                          src={feedback.video_url}
                          controls
                          playsInline
                          preload="metadata"
                          className="w-100 rounded"
                        >
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
    </Container>
  );
};

export default ExerciseFeedbackList;
